import { AttentionReportsSkeleton } from "../../../features/reports/components/attention-reports-skeleton";
import { ReportSceneSkeleton } from "../../../features/reports/components/reports-scene-skeleton";
import { ReportsLayout } from "@/components/layouts";
import { FullPageLoader } from "@/components/fallbacks";
import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import ResourceNotFound from "@/assets/fallbacks/404-reports.png";
import { ErrorBoundary } from "@sentry/react";
import { useEditorStore } from "@/features/studies/stores/use-editor-store";

/* Prop Types */
interface ReportsErrorBoundaryProps {
  studyId: string;
  projectId: string;
  children: ReactNode;
  studyType?: "asset" | "scene";
}

/* ReportsErrorBoundary component
 * This component wraps the reports pages to handle errors and provide a fallback UI
 * this is because we throw an error on query errors in the reports pages
 */
export const ReportsErrorBoundary = ({ studyId, projectId, children, studyType = "asset" }: ReportsErrorBoundaryProps) => {
  /* Hooks */
  const [, navigate] = useLocation();

  /* Actions */
  const { reset } = useEditorStore((state) => state.editorActions);

  /* Handlers */
  const handleGoHome = () => {
    // Reset the editor state
    reset();

    // Navigate to the home page
    navigate("/", { replace: true });
  };

  /* Render */
  return (
    <ErrorBoundary
      fallback={({ error }) => {
        const errorCode = (error as any)?.response?.status || "000";
        return (
          <ReportsLayout projectId={projectId} studyId={studyId} hideSidebar={studyType === "scene"}>
            <FullPageLoader>
              <div className="flex flex-col justify-center gap-4 items-center text-center">
                <img src={ResourceNotFound} alt="Page not found" className="w-36 mb-2" />
                <h2>We&apos;re having trouble loading your study ({errorCode})</h2>
                <p className="text-base-content/70">Please contact our support team via support@dragonflyai.co.</p>
                <Button onClick={handleGoHome}>Go to Home</Button>
              </div>
            </FullPageLoader>
            {studyType === "scene" ? (
              <div className="pl-2">
                <ReportSceneSkeleton title="Visibility" />
              </div>
            ) : (
              <AttentionReportsSkeleton />
            )}
          </ReportsLayout>
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
